import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
    MessageCircle,
    CheckCircle2,
    XCircle,
    Clock,
    Send,
    User,
    Tag,
    Calendar,
    Loader2,
    ChevronRight,
    HelpCircle,
    Paperclip,
    File,
    Download,
    Eye,
    X,
    Image as ImageIcon,
    Reply,
    ChevronDown,
    Mic,
    Volume2
} from 'lucide-react';
import VoiceRecorder from '../../components/Chat/VoiceRecorder';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { getCategories, getSubcategoriesByCategory } from '../../redux/masterSlice';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import MasterLabel from '../../components/common/MasterLabel';
import axios from 'axios';
import baseURL from '../../utils/baseURL';
import {
    connectSupportSocket,
    disconnectSupportSocket,
    isSupportSocketConnected,
    userSocketEvents
} from '../../services/supportSocketService';

import getFileURL from '../../utils/fileURL';


const PatientDoctorSupport = () => {
    const { t, i18n } = useTranslation(['support']);
    console.log('🔵 PatientDoctorSupport component mounting/rendering');

    const location = useLocation();
    const dispatch = useDispatch();
    const userRole = localStorage.getItem('Role') || 'patient';
    const userId = localStorage.getItem('ID');

    console.log('👤 User Info:', { userRole, userId });
    console.log('🔌 Socket connected:', isSupportSocketConnected());

    const { supportCategories, supportCategoryLoading, subcategoryLoading } = useSelector((state) => state.master);

    const [activeTab, setActiveTab] = useState('create');
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [messageInput, setMessageInput] = useState('');
    const [messageError, setMessageError] = useState('');
    const [tickets, setTickets] = useState({
        activeTickets: [],
        closedTickets: []
    });
    const [isLoadingTickets, setIsLoadingTickets] = useState(true);

    console.log("tickets:", selectedTicket);

    // Category selection state
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubCategory, setSelectedSubCategory] = useState(null);
    const hasInitialFetch = useRef(false);
    const loadingTimeoutRef = useRef(null);
    const visibilityListenerRef = useRef(null);

    const messagesEndRef = useRef(null);
    const chatAreaRef = useRef(null);
    const fileInputRef = useRef(null);

    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [replyToMessage, setReplyToMessage] = useState(null);
    const [showScrollBottom, setShowScrollBottom] = useState(false);

    const handleScroll = () => {
        if (!chatAreaRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = chatAreaRef.current;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        setShowScrollBottom(!isNearBottom);
    };

    // Connect socket when support page is opened, disconnect when leaving
    useEffect(() => {
        console.log('🔌 Support page opened - connecting socket...');

        if (!userId || !userRole) {
            console.error('❌ User ID or Role not found');
            return;
        }

        // Connect socket when page opens
        try {
            console.log('📡 Connecting support socket for:', { role: userRole, id: userId });
            connectSupportSocket({ role: userRole, id: userId });
        } catch (error) {
            console.error('❌ Error connecting socket:', error);
            toast.warning(t('support.failed_connect_support'));
        }

        // Cleanup: Disconnect socket when leaving the page
        return () => {
            console.log('🚪 Leaving support page - disconnecting socket...');
            disconnectSupportSocket();
            console.log('✅ Support socket disconnected');
        };
    }, [userId, userRole]); // Re-run if user changes

    // Setup socket event listeners (socket connection is managed globally)
    useEffect(() => {
        console.log('🔄 useEffect RUNNING - Setting up socket event listeners');
        console.log('📊 Socket connected:', isSupportSocketConnected());
        console.log('📊 userId:', userId);
        console.log('📊 userRole:', userRole);

        if (!userId) {
            console.error('❌ User ID not found');
            toast.warning(t('support.user_id_not_found', 'User ID not found'));
            return;
        }

        // Always clean up old listeners before setting up new ones
        console.log('🧹 Cleaning up old listeners before setup');
        userSocketEvents.removeListener('userTickets');
        userSocketEvents.removeListener('userTicketListUpdate');
        userSocketEvents.removeListener('supportTicketInitialized');
        userSocketEvents.removeListener('supportTicketJoined');
        userSocketEvents.removeListener('supportHistory');
        userSocketEvents.removeListener('receiveSupportMessage');
        userSocketEvents.removeListener('ticketAssigned');
        userSocketEvents.removeListener('ticketStatusChanged');
        userSocketEvents.removeListener('supportTicketClosed');
        userSocketEvents.removeListener('supportTicketAlreadyOpen');
        userSocketEvents.removeListener('supportError');

        console.log('🎧 Setting up socket event listeners...');

        // Ensure socket is connected (should be connected by the previous useEffect)
        if (!isSupportSocketConnected()) {
            console.warn('⚠️ Socket not connected, attempting connection...');
            try {
                connectSupportSocket({ role: userRole, id: userId });
            } catch (error) {
                console.error('❌ Error connecting socket:', error);
            }
        }

        try {
            setIsLoadingTickets(true);

            // Listen for user tickets
            // Backend sends array directly, not wrapped in an object
            console.log('🎧 Attaching onUserTickets listener...');
            userSocketEvents.onUserTickets((data) => {
                console.log('📥 ===== RECEIVED USER TICKETS (LISTENER FIRED) =====');
                console.log('📥 Received user tickets:', data);
                console.log('📥 Data type:', typeof data, 'Is array:', Array.isArray(data));

                if (data === null || data === undefined) {
                    console.warn('⚠️ Received null/undefined user tickets, setting to empty arrays');
                    setTickets({
                        activeTickets: [],
                        closedTickets: []
                    });
                    setIsLoadingTickets(false);
                    console.log('📥 ===== END USER TICKETS =====');
                    return;
                }

                const ticketsArray = Array.isArray(data) ? data : [];
                const activeTickets = ticketsArray.filter(t => t.status !== 'closed');
                const closedTickets = ticketsArray.filter(t => t.status === 'closed');

                console.log('✅ Setting tickets:', {
                    total: ticketsArray.length,
                    activeCount: activeTickets.length,
                    closedCount: closedTickets.length
                });
                setTickets({
                    activeTickets,
                    closedTickets
                });
                setIsLoadingTickets(false);
                console.log('📥 ===== END USER TICKETS =====');
            });

            // IMPORTANT: Request tickets from backend AFTER listeners are set up
            // Wait a moment to ensure all listeners are fully attached
            setTimeout(() => {
                console.log('📤 Requesting user tickets from backend...');
                console.log('   User ID:', userId);
                console.log('   User Role:', userRole);
                console.log('   Socket connected:', isSupportSocketConnected());

                userSocketEvents.requestTickets();

                console.log('✅ Ticket request sent, waiting for responses...');

                // Retry request after 2 seconds if no data received
                setTimeout(() => {
                    const hasData = tickets.activeTickets.length > 0 || tickets.closedTickets.length > 0;
                    if (!hasData && isSupportSocketConnected()) {
                        console.log('🔄 No data received, retrying ticket request...');
                        userSocketEvents.requestTickets();
                    }
                }, 2000);
            }, 500); // Small delay to ensure listeners are attached

            // Fallback: Stop loading after timeout if no tickets received
            // This handles case where socket connected before listeners were set up
            loadingTimeoutRef.current = setTimeout(() => {
                console.log('⏱️ Loading timeout - stopping loading state');
                console.log('   Final ticket counts:', {
                    active: tickets.activeTickets.length,
                    closed: tickets.closedTickets.length
                });
                setIsLoadingTickets(false);
            }, 3000); // 3 second timeout

            // Listen for ticket list updates
            userSocketEvents.onUserTicketListUpdate((data) => {
                console.log('User ticket list updated:', data);
                if (data.tickets) {
                    const activeTickets = data.tickets.filter(t => t.status !== 'closed');
                    const closedTickets = data.tickets.filter(t => t.status === 'closed');

                    setTickets({
                        activeTickets,
                        closedTickets
                    });
                }
            });

            // Listen for ticket initialized
            // Backend sends ticket object directly
            userSocketEvents.onTicketInitialized((ticket) => {
                console.log('✅ Ticket initialized:', ticket);

                // Add to active tickets and immediately switch to active tab
                setTickets(prev => ({
                    ...prev,
                    activeTickets: [ticket, ...prev.activeTickets]
                }));

                // Reset category selection
                setSelectedCategory(null);
                setSelectedSubCategory(null);

                // Switch to active tab and select the ticket
                setActiveTab('active');
                setSelectedTicket(ticket);

                // Join the ticket room to receive messages
                userSocketEvents.joinTicket(ticket.ticketId, userId, userRole);

                console.log('🔄 Switched to active tab with ticket:', ticket.ticketId);
            });

            // Listen for duplicate ticket (already open)
            userSocketEvents.onTicketAlreadyOpen((data) => {
                console.log('⚠️ Ticket already open:', data);
                const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
                const message = lang === 'fr' ? data.message_fr : data.message_en;

                Swal.fire({
                    icon: 'warning',
                    title: t('support.ticket_already_open', 'Ticket Already Open'),
                    text: message || t('support.ticket_already_open_text', 'You already have an open ticket for this category.'),
                    confirmButtonColor: '#4ABDBD',
                    confirmButtonText: t('support.view_ticket', 'View Ticket'),
                    showCancelButton: true,
                    cancelButtonColor: '#6B7280',
                    cancelButtonText: t('support.close', 'Close'),
                    reverseButtons: true
                }).then((result) => {
                    if (result.isConfirmed && data.ticketId) {
                        // Navigate to the existing open ticket
                        const existingTicket = tickets.activeTickets.find(t => t.ticketId === data.ticketId);
                        if (existingTicket) {
                            setSelectedTicket(existingTicket);
                            setActiveTab('active');
                            userSocketEvents.joinTicket(data.ticketId, userId, userRole);
                        } else {
                            // Ticket not in local state yet, set minimal info and join
                            setSelectedTicket({ ticketId: data.ticketId, status: 'open' });
                            setActiveTab('active');
                            userSocketEvents.joinTicket(data.ticketId, userId, userRole);
                        }
                        // Reset category selection
                        setSelectedCategory(null);
                        setSelectedSubCategory(null);
                    }
                });
            });

            // Listen for support error (fallback for duplicate ticket)
            userSocketEvents.onSupportError((data) => {
                console.log('❌ Support error:', data);
                if (data.code === 'SUPPORT_TICKET_ALREADY_OPEN') {
                    // Already handled by supportTicketAlreadyOpen, skip if Swal is already open
                    if (Swal.isVisible()) return;

                    const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
                    const message = lang === 'fr' ? data.message_fr : data.message_en;

                    toast.warn(message || t('support.ticket_already_open_text', 'You already have an open ticket for this category.'));
                } else {
                    // Generic support error
                    const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
                    const message = lang === 'fr' ? (data.message_fr || data.message) : (data.message_en || data.message);

                    toast.warn(message || t('common.something_went_wrong', 'Something went wrong.'));
                }
            });

            // Listen for ticket joined
            // Backend sends ticket summary with lastMessage, not full messages array
            userSocketEvents.onTicketJoined((summary) => {
                console.log('🎫 Ticket joined:', summary);
                // Initialize with empty messages array (will be populated by history)
                setSelectedTicket(prev => ({
                    ...prev,
                    ...summary,
                    messages: [] // Start with empty, history will populate
                }));
                // Fetch full message history immediately
                console.log('📜 Fetching message history for ticket:', summary.ticketId);
                userSocketEvents.fetchHistory(summary.ticketId, 1);
            });

            // Listen for message history
            userSocketEvents.onHistory((data) => {
                console.log('📜 Received message history:', data);
                if (data.ticketId && data.messages) {
                    // Normalize and sort messages by timestamp
                    const normalizedMessages = data.messages.map(msg => ({
                        _id: msg._id,
                        messageId: msg.messageId,
                        senderRole: msg.senderRole,
                        message: msg.message,
                        time: msg.createdAt || new Date().toISOString(),
                        createdAt: msg.createdAt,
                        isRead: msg.isRead || false,
                        attachments: msg.attachments || [],
                        replyTo: msg.replyTo || null,
                        senderName: msg.senderName || null,
                        // Preserve system event payload (assignment / reassignment banners).
                        systemEvent: msg.systemEvent || null,
                    }));

                    // Sort by timestamp to ensure correct chronological order
                    const sortedMessages = normalizedMessages.sort((a, b) => {
                        const timeA = new Date(a.createdAt || a.time || 0).getTime();
                        const timeB = new Date(b.createdAt || b.time || 0).getTime();
                        return timeA - timeB;
                    });

                    setSelectedTicket(prev =>
                        prev?.ticketId === data.ticketId
                            ? { ...prev, messages: sortedMessages }
                            : prev
                    );

                    // Also propagate the history into the tickets list so that when a
                    // new `receiveSupportMessage` arrives and the handler syncs the
                    // selectedTicket from the list, the history isn't dropped.
                    setTickets(prev => {
                        const patchList = (list) =>
                            list.map((t) =>
                                t.ticketId === data.ticketId
                                    ? { ...t, messages: sortedMessages }
                                    : t
                            );
                        return {
                            ...prev,
                            activeTickets: patchList(prev.activeTickets || []),
                            closedTickets: patchList(prev.closedTickets || []),
                        };
                    });
                }
            });

            // Listen for new messages
            console.log('🎧 Attaching onReceiveMessage listener...');
            userSocketEvents.onReceiveMessage((data) => {
                console.log('📬 ===== RECEIVED MESSAGE EVENT =====');
                console.log('📬 Full message data:', JSON.stringify(data, null, 2));
                console.log('📬 Ticket ID:', data.ticketId);
                console.log('📬 Selected ticket ID:', selectedTicket?.ticketId);

                if (!data || !data.ticketId) {
                    console.error('❌ Invalid message data:', data);
                    return;
                }

                // Normalize message structure according to guide
                // Backend sends: { _id, messageId, ticketId, senderId, senderRole, message, isRead, createdAt }
                const normalizedMessage = {
                    _id: data._id,
                    messageId: data.messageId || `msg_${Date.now()}`,
                    senderRole: data.senderRole,
                    senderId: data.senderId,
                    message: data.message,
                    time: data.createdAt || new Date().toISOString(),
                    createdAt: data.createdAt || new Date().toISOString(),
                    isRead: data.isRead || false,
                    attachments: data.attachments || [],
                    replyTo: data.replyTo || null,
                    senderName: data.senderName || null,
                    // Preserve system event payload so the banner renders correctly.
                    systemEvent: data.systemEvent || null,
                };

                console.log('📬 Normalized message:', normalizedMessage);

                // ALWAYS update tickets list first (even if ticket not selected)
                let updatedTicketWithMessage = null;
                setTickets(prev => {
                    let ticketFound = false;
                    const updatedTickets = prev.activeTickets.map(ticket => {
                        if (ticket.ticketId === data.ticketId) {
                            ticketFound = true;
                            console.log('✅ Found ticket in list, adding message');

                            // Check for duplicates by messageId only (not by content)
                            const messageExists = ticket.messages?.some(
                                msg => msg.messageId === normalizedMessage.messageId
                            );

                            if (messageExists) {
                                console.log('⚠️ Message already in ticket list (same messageId), skipping');
                                updatedTicketWithMessage = ticket; // Still return the ticket for selectedTicket update
                                return ticket;
                            }

                            // Remove temp message if exists (replace with real one)
                            const filteredMessages = (ticket.messages || []).filter(
                                msg => !(msg.messageId?.startsWith('temp_') &&
                                    msg.message === normalizedMessage.message &&
                                    msg.senderRole === normalizedMessage.senderRole)
                            );

                            const updatedMessages = [...filteredMessages, normalizedMessage];
                            // Sort messages by timestamp
                            const sortedMessages = updatedMessages.sort((a, b) => {
                                const timeA = new Date(a.createdAt || a.time || 0).getTime();
                                const timeB = new Date(b.createdAt || b.time || 0).getTime();
                                return timeA - timeB;
                            });

                            console.log('✅ Updated ticket messages count:', sortedMessages.length);
                            updatedTicketWithMessage = { ...ticket, messages: sortedMessages };
                            return updatedTicketWithMessage;
                        }
                        return ticket;
                    });

                    if (!ticketFound) {
                        console.warn('⚠️ Ticket not found in activeTickets list:', data.ticketId);
                        console.log('📋 Current activeTickets:', prev.activeTickets.map(t => t.ticketId));
                    }

                    return {
                        ...prev,
                        activeTickets: updatedTickets
                    };
                });

                // Update selected ticket - ALWAYS sync with updated ticket from list if it matches
                // This ensures selectedTicket is always in sync, even if it was null before
                setSelectedTicket(prev => {
                    // If selectedTicket is null but we're on active tab, try to restore it
                    if (!prev && activeTab === 'active' && updatedTicketWithMessage) {
                        console.log('🔄 Restoring selectedTicket from updated ticket (was null)');
                        return updatedTicketWithMessage;
                    }

                    // If selectedTicket exists and matches, update it
                    if (prev && prev.ticketId === data.ticketId) {
                        console.log('✅ Ticket is selected, updating selected ticket state');

                        // Use the updated ticket from list to ensure consistency
                        if (updatedTicketWithMessage) {
                            console.log('✅ Syncing selectedTicket with updated ticket from list');
                            return updatedTicketWithMessage;
                        }

                        // Fallback: update manually if ticket not found in list update
                        // Check if message already exists by messageId only
                        const messageExists = prev.messages?.some(
                            msg => msg.messageId === normalizedMessage.messageId
                        );

                        if (messageExists) {
                            console.log('⚠️ Message already exists in selected ticket (same messageId), skipping duplicate');
                            return prev;
                        }

                        // Remove temporary message if exists (replace with real one)
                        const filteredMessages = (prev.messages || []).filter(
                            msg => !(msg.messageId?.startsWith('temp_') &&
                                msg.message === normalizedMessage.message &&
                                msg.senderRole === normalizedMessage.senderRole &&
                                Math.abs(new Date(msg.createdAt || msg.time).getTime() - new Date(normalizedMessage.createdAt || normalizedMessage.time).getTime()) < 5000)
                        );

                        const updatedMessages = [...filteredMessages, normalizedMessage];
                        // Sort messages by timestamp to ensure correct order
                        const sortedMessages = updatedMessages.sort((a, b) => {
                            const timeA = new Date(a.createdAt || a.time || 0).getTime();
                            const timeB = new Date(b.createdAt || b.time || 0).getTime();
                            return timeA - timeB;
                        });

                        console.log('✅ Updated selected ticket messages count:', sortedMessages.length);
                        console.log('📝 Message added to selectedTicket:', normalizedMessage);
                        return {
                            ...prev,
                            messages: sortedMessages
                        };
                    }

                    // If different ticket selected or no ticket selected
                    if (!prev) {
                        console.log('ℹ️ No ticket selected, message added to ticket list only');
                    } else {
                        console.log('ℹ️ Different ticket selected, message added to ticket list only');
                    }
                    return prev;
                });

                console.log('📬 ===== END RECEIVED MESSAGE =====');
            });

            // Listen for ticket assigned
            userSocketEvents.onTicketAssigned((data) => {
                console.log('Ticket assigned:', data);

                // Update ticket status
                setTickets(prev => ({
                    ...prev,
                    activeTickets: prev.activeTickets.map(ticket =>
                        ticket.ticketId === data.ticketId
                            ? { ...ticket, status: 'assigned', assignedTo: data.adminName }
                            : ticket
                    )
                }));

                if (selectedTicket?.ticketId === data.ticketId) {
                    setSelectedTicket(prev => ({
                        ...prev,
                        status: 'assigned',
                        assignedTo: data.adminName
                    }));
                }
            });

            // Listen for ticket closed (from ticket room)
            userSocketEvents.onTicketClosed((data) => {
                console.log('Ticket closed:', data);

                // Move ticket to closed list
                setTickets(prev => {
                    const closedTicket = prev.activeTickets.find(t => t.ticketId === data.ticketId);
                    return {
                        activeTickets: prev.activeTickets.filter(t => t.ticketId !== data.ticketId),
                        closedTickets: closedTicket
                            ? [{ ...closedTicket, status: 'closed', closedAt: data.closedAt }, ...prev.closedTickets]
                            : prev.closedTickets
                    };
                });

                // Use functional check to avoid stale closure of selectedTicket
                setSelectedTicket(prev => {
                    if (prev?.ticketId === data.ticketId) {
                        setActiveTab('closed');
                        return null;
                    }
                    return prev;
                });
            });

            // Listen for ticket status changes (from user-specific room support_user_{userId})
            // This catches close/reassign events even when user isn't in the ticket room
            userSocketEvents.onTicketStatusChanged((data) => {
                console.log('Ticket status changed:', data);

                if (data.status === 'closed') {
                    setTickets(prev => {
                        const closedTicket = prev.activeTickets.find(t => t.ticketId === data.ticketId);
                        if (!closedTicket) return prev; // already handled by onTicketClosed
                        return {
                            activeTickets: prev.activeTickets.filter(t => t.ticketId !== data.ticketId),
                            closedTickets: [{ ...closedTicket, status: 'closed' }, ...prev.closedTickets]
                        };
                    });

                    // Use functional check to avoid stale closure
                    setSelectedTicket(prev => {
                        if (prev?.ticketId === data.ticketId) {
                            setActiveTab('closed');
                            return null;
                        }
                        return prev;
                    });
                } else if (data.status === 'open' && data.assignedTo) {
                    // Ticket reassigned to a new admin — update the ticket in active list
                    setTickets(prev => ({
                        ...prev,
                        activeTickets: prev.activeTickets.map(t =>
                            t.ticketId === data.ticketId
                                ? { ...t, assignedTo: data.assignedTo, adminName: data.adminName }
                                : t
                        )
                    }));
                }
            });

        } catch (error) {
            console.error('Error initializing socket:', error);
            toast.warning(t('support.failed_connect_support'));
            setIsLoadingTickets(false);
        }

        // Cleanup on unmount
        return () => {
            // Clear loading timeout if still pending
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
            }

            // Only remove listeners, don't disconnect socket
            // Socket should persist across component remounts (React StrictMode)
            // It will be disconnected on logout or app close
            console.log('🧹 Cleaning up socket listeners (not disconnecting)');
            userSocketEvents.removeListener('userTickets');
            userSocketEvents.removeListener('userTicketListUpdate');
            userSocketEvents.removeListener('supportTicketInitialized');
            userSocketEvents.removeListener('supportTicketJoined');
            userSocketEvents.removeListener('supportHistory');
            userSocketEvents.removeListener('receiveSupportMessage');
            userSocketEvents.removeListener('ticketAssigned');
            userSocketEvents.removeListener('ticketStatusChanged');
            userSocketEvents.removeListener('supportTicketClosed');
            userSocketEvents.removeListener('supportTicketAlreadyOpen');
            userSocketEvents.removeListener('supportError');

            // Don't disconnect here - causes issues with React StrictMode
            // disconnectSupportSocket();
        };
    }, [location.key, userRole, userId]); // Re-run when navigating to this page

    // Clear selected ticket if it no longer exists in active/closed lists
    useEffect(() => {
        if (selectedTicket) {
            const existsInActive = tickets.activeTickets.some(t => t.ticketId === selectedTicket.ticketId);
            const existsInClosed = tickets.closedTickets.some(t => t.ticketId === selectedTicket.ticketId);

            if (!existsInActive && !existsInClosed) {
                setSelectedTicket(null);
            } else if (!existsInActive && existsInClosed && activeTab !== 'closed') {
                // Ticket moved to closed — switch tab and clear selection
                setActiveTab('closed');
                setSelectedTicket(null);
            }
        }
    }, [tickets, selectedTicket, activeTab]);

    // Handle browser tab visibility changes
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.log('👁️ Tab became visible - refreshing tickets...');

                // Check if socket is still connected
                if (!isSupportSocketConnected()) {
                    console.warn('⚠️ Socket disconnected while tab was hidden, reconnecting...');
                    if (userId && userRole) {
                        connectSupportSocket({ role: userRole, id: userId });
                    }
                }

                // Request fresh ticket data
                userSocketEvents.requestTickets();

                // If a ticket is selected, refresh its data by rejoining
                if (selectedTicket?.ticketId) {
                    console.log('🔄 Refreshing selected ticket:', selectedTicket.ticketId);
                    userSocketEvents.joinTicket(selectedTicket.ticketId, userId, userRole);
                }
            }
        };

        // Add visibility change listener
        document.addEventListener('visibilitychange', handleVisibilityChange);
        visibilityListenerRef.current = handleVisibilityChange;

        return () => {
            // Remove visibility change listener
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [selectedTicket, userId, userRole]); // Re-run when selectedTicket, userId, or userRole changes

    // Fetch categories on mount
    useEffect(() => {
        if (hasInitialFetch.current) return;
        hasInitialFetch.current = true;
        const roleParam = userRole === 'doctor' ? 'doctor' : userRole === 'patient' ? 'patient' : null;
        dispatch(getCategories(roleParam));
    }, [userRole, dispatch]);

    // Normalize categories
    const normalizeCategories = (list) =>
        (list || []).map((cat) => ({
            ...cat,
            viewableBy: cat.viewableBy || cat.role || 'both',
            subCategories: (cat.subCategories || []).map((sub) => ({
                ...sub,
                viewableBy: sub.viewableBy || sub.role || 'both',
            })),
        }));

    const normalizedCategories = normalizeCategories(supportCategories || []);

    useEffect(() => {
        scrollToBottom();
    }, [selectedTicket?.messages]);

    // Sync selectedTicket with tickets list when messages are updated
    useEffect(() => {
        if (selectedTicket?.ticketId) {
            const ticketFromList = tickets.activeTickets.find(t => t.ticketId === selectedTicket.ticketId);
            if (ticketFromList && ticketFromList.messages) {
                const listMessageCount = ticketFromList.messages.length;
                const selectedMessageCount = selectedTicket.messages?.length || 0;

                // If list has more messages, sync them
                if (listMessageCount > selectedMessageCount) {
                    console.log('🔄 Syncing selectedTicket with tickets list (messages updated)');
                    console.log('📊 List has', listMessageCount, 'messages, selected has', selectedMessageCount);
                    setSelectedTicket(prev => ({
                        ...prev,
                        messages: ticketFromList.messages
                    }));
                } else if (listMessageCount < selectedMessageCount) {
                    // If selected has more messages, sync back to list (shouldn't happen, but just in case)
                    console.log('🔄 SelectedTicket has more messages than list, this is unexpected');
                }
            } else if (selectedTicket.messages && selectedTicket.messages.length > 0) {
                // Ticket not in list but has messages - this can happen temporarily
                console.log('ℹ️ Selected ticket not in activeTickets list but has messages');
            }
        }
    }, [tickets.activeTickets]);

    const scrollToMessage = (msgId) => {
        if (!msgId) return;
        const element = document.getElementById(`message-${msgId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('bg-[#4ABDBD]/10', 'ring-[#4ABDBD]/50');
            setTimeout(() => {
                element.classList.remove('bg-[#4ABDBD]/10', 'ring-[#4ABDBD]/50');
            }, 2000);
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            console.warn('Invalid date:', dateString);
            return '';
        }
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return t('support.date_just_now');
        if (diffMins < 60) return t('support.date_minutes_ago', { minutes: diffMins, plural: diffMins > 1 ? 's' : '' });
        if (diffHours < 24) return t('support.date_hours_ago', { hours: diffHours, plural: diffHours > 1 ? 's' : '' });
        if (diffDays < 7) return t('support.date_days_ago', { days: diffDays, plural: diffDays > 1 ? 's' : '' });
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Handle category selection with API fetch for subcategories
    const handleCategorySelect = async (category) => {
        setSelectedCategory(category);
        setSelectedSubCategory(null);

        const categoryId = category.categoryId || category.id;
        if (!categoryId) return;

        const roleParam = userRole === 'doctor' ? 'doctor' : userRole === 'patient' ? 'patient' : null;

        try {
            const result = await dispatch(getSubcategoriesByCategory({ categoryId, role: roleParam })).unwrap();
            const subcategories = result?.data?.subCategories || result?.data?.subcategories || result?.data?.data || [];
            if (subcategories.length) {
                setSelectedCategory({ ...category, subCategories: subcategories });
            }
        } catch (error) {
            console.error('Error fetching subcategories:', error);
        }
    };

    // Handle subcategory selection
    const handleSubCategorySelect = (subCategory) => {
        setSelectedSubCategory(subCategory);
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length + selectedFiles.length > 3) {
            toast.warning(t('chat.max_files_limit', 'Maximum 3 files allowed'));
            return;
        }

        const validFiles = files.filter(file => {
            const isLt10M = file.size / 1024 / 1024 < 10;
            if (!isLt10M) {
                toast.warning(t('chat.file_too_large', 'File size must be less than 10MB'));
            }
            return isLt10M;
        });

        setSelectedFiles(prev => [...prev, ...validFiles]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleDownload = (fileUrl) => {
        if (!fileUrl) return;
        const fileName = fileUrl.split('/').pop();
        const downloadUrl = `${baseURL}/support/message/download/${fileName}`;
        window.open(downloadUrl, '_blank');
    };

    // Handle start chat - create new ticket via socket
    const handleStartChat = () => {
        if (selectedCategory && selectedSubCategory) {
            const categoryId = selectedCategory.categoryId || selectedCategory.id;
            const subCategoryId = selectedSubCategory.subCategoryId || selectedSubCategory.id;

            const ticketData = {
                userId,
                userRole,
                categoryId,
                subCategoryId,
                subject: selectedSubCategory.name // Backend uses this for ticket subject
            };

            console.log('🎫 Creating ticket with data:', ticketData);
            console.log('🔍 Socket connected?', isSupportSocketConnected());

            // FAILSAFE: Ensure socket is connected before creating ticket
            if (!isSupportSocketConnected()) {
                console.error('❌ Socket not connected! Attempting to reconnect...');
                console.warn('⚠️ Connecting to support service...');

                // Try to connect
                connectSupportSocket({ role: userRole, id: userId });

                // Wait a moment for connection, then try again
                setTimeout(() => {
                    if (isSupportSocketConnected()) {
                        console.log('✅ Socket reconnected, creating ticket');
                        userSocketEvents.initTicket(ticketData);
                    } else {
                        console.error('❌ Socket still not connected after retry');
                        toast.warning(t('support.connect_failed', 'Failed to connect to support service. Please refresh the page.'));
                    }
                }, 1000);
                return;
            }

            // Emit init ticket event
            console.log('📤 Emitting initTicket event with data:', ticketData);
            userSocketEvents.initTicket(ticketData);
        } else {
            toast.warning(t('support.select_both_cat', 'Please select both category and subcategory'));
        }
    };

    const handleSelectTicket = (ticket) => {
        // Always get the latest ticket from the list to ensure we have the latest messages
        const latestTicket = tickets.activeTickets.find(t => t.ticketId === ticket.ticketId) ||
            tickets.closedTickets.find(t => t.ticketId === ticket.ticketId) ||
            ticket;

        console.log('🎫 Selecting ticket:', latestTicket.ticketId, 'with', latestTicket.messages?.length || 0, 'messages');
        setSelectedTicket(latestTicket);

        // Join the ticket room to receive messages
        userSocketEvents.joinTicket(latestTicket.ticketId, userId, userRole);
    };

    // Validate message input
    const validateMessage = (value) => {
        // Remove any potentially dangerous characters
        const sanitized = value.replace(/[<>]/g, '');

        // Check length
        if (sanitized.length > 1000) {
            return t('support.message_too_long', { length: 1000 });
        }

        // Check for only whitespace
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            return t('support.message_whitespace_only');
        }

        return '';
    };

    const handleMessageChange = (e) => {
        const value = e.target.value;

        // Clear error if user is typing
        if (messageError && value.length < messageInput.length) {
            setMessageError('');
        }

        // Validate input
        const error = validateMessage(value);
        if (error) {
            setMessageError(error);
        } else {
            // Sanitize input - remove dangerous characters
            const sanitized = value.replace(/[<>]/g, '');
            // Enforce max length
            const finalValue = sanitized.length > 1000
                ? sanitized.substring(0, 1000)
                : sanitized;
            setMessageInput(finalValue);
            setMessageError('');
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();

        // Validate message before sending (only if text exists)
        if (messageInput.trim()) {
            const error = validateMessage(messageInput);
            if (error) {
                setMessageError(error);
                return;
            }
        }

        if ((!messageInput.trim() && selectedFiles.length === 0) || !selectedTicket || isUploading) return;

        // Verify socket is connected
        if (!isSupportSocketConnected()) {
            toast.warning(t('support.not_connected_server', 'Not connected to server. Please refresh the page.'));
            return;
        }

        const messageText = messageInput.trim();
        const currentFiles = [...selectedFiles];

        if (currentFiles.length > 0) {
            setIsUploading(true);
            try {
                const formData = new FormData();
                formData.append('ticketId', selectedTicket.ticketId);
                formData.append('senderId', userId);
                formData.append('senderRole', userRole);
                formData.append('message', messageText);
                if (replyToMessage) {
                    formData.append('replyTo', replyToMessage._id);
                }

                currentFiles.forEach(file => {
                    formData.append('files', file); // API expects 'images' field for support
                });

                await axios.post(`${baseURL}/support/message/send`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                // Reset input ONLY after successful upload
                setMessageInput('');
                setSelectedFiles([]);
                setMessageError('');
                setReplyToMessage(null);

                // Note: The message will be received via socket
            } catch (error) {
                console.error("Error sending support message with files:", error.response?.data || error.message);
                toast.warning(t('support.send_failed', 'Failed to send message'));
                // Keep the input and files so user can retry
            } finally {
                setIsUploading(false);
            }
        } else {
            // Reset input for text-only messages
            setMessageInput('');
            setSelectedFiles([]);
            setMessageError('');

            const messageData = {
                ticketId: selectedTicket.ticketId,
                senderId: userId,
                senderRole: userRole,
                message: messageText
            };
            if (replyToMessage) {
                messageData.replyTo = replyToMessage._id;
            }
            setReplyToMessage(null);

            // Fire the socket send. The server echoes back via `receiveSupportMessage`
            // which the onReceiveMessage listener below handles. We intentionally DO NOT
            // do an optimistic update here: the previous optimistic code maintained a
            // temp message in selectedTicket only (not in tickets.activeTickets), then
            // tried to filter it out when the echo arrived. That dual-state sync had
            // race conditions that produced duplicate messages on the very first send
            // after a ticket was created. Skipping the optimistic update adds ~1 socket
            // roundtrip of perceived latency but eliminates the duplicate bug entirely.
            userSocketEvents.sendMessage(messageData);
        }
    };

    const handleCloseTicket = async () => {
        if (!selectedTicket) return;

        const result = await Swal.fire({
            title: t('support.close_ticket_confirm'),
            text: t('support.close_ticket_text', 'Are you sure you want to close this ticket? This action cannot be undone.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#6B7280',
            confirmButtonText: t('support.yes_close', 'Yes, close it!'),
            cancelButtonText: t('support.cancel', 'Cancel'),
            reverseButtons: true
        });

        if (result.isConfirmed) {

            console.log('🔒 Closing ticket:', selectedTicket.ticketId);
            // Emit close ticket event via socket
            userSocketEvents.closeTicket(selectedTicket.ticketId, userId);
            setActiveTab('closed');
        }
    };

    const getStatusBadge = (status) => {
        const configs = {
            open: { label: t('support.status_open'), color: 'bg-blue-100 text-blue-800', icon: Clock },
            assigned: { label: t('support.status_assigned'), color: 'bg-yellow-100 text-yellow-800', icon: Clock },
            closed: { label: t('support.status_closed'), color: 'bg-gray-100 text-gray-800', icon: CheckCircle2 }
        };
        const config = configs[status?.toLowerCase()] || configs.open;
        const Icon = config.icon;
        return (
            <span data-testid={`support-status-badge-${status?.toLowerCase() || 'open'}`} className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                <Icon size={12} data-testid={`support-status-icon-${status?.toLowerCase() || 'open'}`} />
                {config.label}
            </span>
        );
    };

    return (
        <>
            <div data-testid="support-container" className="h-full flex flex-col bg-white rounded-2xl shadow-md overflow-hidden">
                {/* Header */}
                <div data-testid="support-header" className="px-6 py-4 ">
                    <h1 data-testid="support-heading" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('support.support_heading')}
                    </h1>
                    {/* <p className="text-sm text-gray-600 mt-1">{t('support.get_help_description')}</p> */}
                </div>

                {/* Tabs */}
                <div data-testid="support-tabs-container" className="border-b border-gray-200 bg-white">
                    <div className="flex gap-1 px-6">
                        <button
                            data-testid="support-tab-create"
                            onClick={() => {
                                setActiveTab('create');
                                setSelectedCategory(null);
                                setSelectedSubCategory(null);
                                setSelectedTicket(null);
                            }}
                            className={`relative px-4 py-3 font-medium text-sm transition-colors ${activeTab === 'create'
                                ? 'text-[#4ABDBD] border-b-2 border-[#4ABDBD]'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            {t('support.create_ticket')}
                        </button>
                        <button
                            data-testid="support-tab-active"
                            onClick={() => {
                                setActiveTab('active');
                                setSelectedTicket(null);
                            }}
                            className={`relative px-4 py-3 font-medium text-sm transition-colors ${activeTab === 'active'
                                ? 'text-[#4ABDBD] border-b-2 border-[#4ABDBD]'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            {t('support.active_tickets')}
                            {tickets.activeTickets.length > 0 && (<span data-testid="support-active-tickets-count" className="ml-2 px-2 py-0.5 bg-[#4ABDBD] text-white text-xs rounded-full">
                                {tickets.activeTickets.length}
                            </span>)}
                        </button>
                        <button
                            data-testid="support-tab-closed"
                            onClick={() => {
                                setActiveTab('closed');
                                setSelectedTicket(null);
                            }}
                            className={`relative px-4 py-3 font-medium text-sm transition-colors ${activeTab === 'closed'
                                ? 'text-[#4ABDBD] border-b-2 border-[#4ABDBD]'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            {t('support.closed_tickets')}
                            {/* {tickets.closedTickets.length > 0 &&(<span className="ml-2 px-2 py-0.5 bg-gray-400 text-white text-xs rounded-full">
                            {tickets.closedTickets.length}
                        </span>)} */}
                        </button>
                    </div>
                </div>

                {/* Content Area */}
                <div data-testid="support-content-area" className="flex-1 flex overflow-hidden">
                    {/* TAB 1: Create Ticket */}
                    {activeTab === 'create' && (
                        <div data-testid="support-create-ticket-section" className="flex-1 overflow-y-auto p-6">
                            <div className="w-full">
                                {!selectedCategory ? (
                                    // Category Selection
                                    <div data-testid="support-category-selection">
                                        <h2 data-testid="support-select-category-heading" className="text-lg font-semibold text-gray-800 mb-4">
                                            {t('support.select_category')}
                                        </h2>
                                        {supportCategoryLoading ? (
                                            <div data-testid="support-categories-loading" className="flex items-center justify-center py-12">
                                                <Loader2 data-testid="support-categories-loader-icon" className="animate-spin text-[#4ABDBD]" size={32} />
                                            </div>
                                        ) : normalizedCategories.length === 0 ? (
                                            <div data-testid="support-no-categories" className="text-center py-12 text-gray-500">
                                                <HelpCircle data-testid="support-no-categories-icon" size={48} className="mx-auto mb-4 text-gray-300" />
                                                <p data-testid="support-no-categories-text">{t('support.no_categories_available')}</p>
                                            </div>
                                        ) : (
                                            <div data-testid="support-categories-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {normalizedCategories.map((category) => (
                                                    <button
                                                        data-testid={`support-category-card-${category.id || category.categoryId}`}
                                                        key={category.id || category.categoryId}
                                                        onClick={() => handleCategorySelect(category)}
                                                        className="p-4 border border-gray-200 rounded-xl hover:border-[#4ABDBD] hover:-translate-y-0.5 hover:shadow-md transition-all text-left bg-white"
                                                    >
                                                        <div className="flex items-start justify-between mb-2">
                                                            <h3 data-testid={`support-category-name-${category.id || category.categoryId}`} className="font-semibold text-gray-800">
                                                                <MasterLabel type="masterSupportCategory" id={category.categoryId || category.id} field="name" fallback={category.name} />
                                                            </h3>
                                                        </div>
                                                        <div className="flex items-center text-sm text-gray-500 mt-2">
                                                            <span data-testid={`support-category-select-subcategory-text-${category.id || category.categoryId}`}>{t('support.select_subcategory')}</span>
                                                            <ChevronRight size={16} className="ml-auto text-gray-400" />
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : !selectedSubCategory ? (
                                    // Subcategory Selection
                                    <div data-testid="support-subcategory-selection">
                                        <button
                                            data-testid="support-back-to-categories-button"
                                            onClick={() => {
                                                setSelectedCategory(null);
                                                setSelectedSubCategory(null);
                                            }}
                                            className="mb-4 text-sm text-[#4ABDBD] hover:text-[#3AA8A8] flex items-center gap-1"
                                        >
                                            <ChevronRight size={16} className="rotate-180" />
                                            {t('support.back_to_categories', 'Back to Categories')}
                                        </button>
                                        <h2 data-testid="support-selected-category-heading" className="text-lg font-semibold text-gray-800 mb-1">
                                            <MasterLabel type="masterSupportCategory" id={selectedCategory.categoryId || selectedCategory.id} field="name" fallback={selectedCategory.name} />
                                        </h2>
                                        {subcategoryLoading ? (
                                            <div data-testid="support-subcategories-loading" className="flex items-center justify-center py-8">
                                                <Loader2 data-testid="support-subcategories-loader-icon" className="animate-spin text-[#4ABDBD]" size={24} />
                                            </div>
                                        ) : !selectedCategory.subCategories || selectedCategory.subCategories.length === 0 ? (
                                            <div data-testid="support-no-subcategories" className="text-center py-8 text-gray-500">
                                                <p data-testid="support-no-subcategories-text">{t('support.no_subcategories', 'No subcategories available for this category.')}</p>
                                            </div>
                                        ) : (
                                            <div data-testid="support-subcategories-list" className="space-y-3">
                                                {selectedCategory.subCategories.map((subCat) => (
                                                    <button
                                                        data-testid={`support-subcategory-card-${subCat.id || subCat.subCategoryId}`}
                                                        key={subCat.id || subCat.subCategoryId}
                                                        onClick={() => handleSubCategorySelect(subCat)}
                                                        className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-[#4ABDBD] hover:bg-[#4ABDBD]/5 transition-all text-left group"
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <h3 data-testid={`support-subcategory-name-${subCat.id || subCat.subCategoryId}`} className="font-semibold text-gray-800 group-hover:text-[#4ABDBD]">
                                                                <MasterLabel type="masterSupportSubCategory" id={subCat.subCategoryId || subCat.id} field="name" fallback={subCat.name} />
                                                            </h3>
                                                            <ChevronRight size={20} className="text-gray-400 group-hover:text-[#4ABDBD]" />
                                                        </div>
                                                        {subCat.description && (
                                                            <p data-testid={`support-subcategory-description-${subCat.id || subCat.subCategoryId}`} className="text-sm text-gray-600 mt-2">
                                                                <MasterLabel type="masterSupportSubCategory" id={subCat.subCategoryId || subCat.id} field="description" fallback={subCat.description} />
                                                            </p>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    // Description and Chat Button
                                    <div data-testid="support-start-chat-section">
                                        <button
                                            data-testid="support-back-to-subcategories-button"
                                            onClick={() => setSelectedSubCategory(null)}
                                            className="mb-4 text-sm text-[#4ABDBD] hover:text-[#3AA8A8] flex items-center gap-1"
                                        >
                                            <ChevronRight size={16} className="rotate-180" />
                                            {t('support.back_to_subcategories', 'Back to Subcategories')}
                                        </button>
                                        <div data-testid="support-start-chat-container" className="bg-gradient-to-br from-[#4ABDBD]/10 to-[#3A9B9B]/10 rounded-xl p-6 border border-[#4ABDBD]/20">
                                            <div className="mb-4">
                                                <h2 data-testid="support-selected-subcategory-category-heading" className="text-lg font-semibold text-gray-800">
                                                    <MasterLabel type="masterSupportCategory" id={selectedCategory.categoryId || selectedCategory.id} field="name" fallback={selectedCategory.name} />
                                                </h2>
                                                <h3 data-testid="support-selected-subcategory-heading" className="text-md font-medium text-[#4ABDBD] mb-3">
                                                    <MasterLabel type="masterSupportSubCategory" id={selectedSubCategory.subCategoryId || selectedSubCategory.id} field="name" fallback={selectedSubCategory.name} />
                                                </h3>
                                                {selectedSubCategory.description && (
                                                    <p data-testid="support-selected-subcategory-description" className="text-gray-600 leading-relaxed">
                                                        <MasterLabel type="masterSupportSubCategory" id={selectedSubCategory.subCategoryId || selectedSubCategory.id} field="description" fallback={selectedSubCategory.description} />
                                                    </p>
                                                )}
                                            </div>
                                            <button
                                                data-testid="support-start-chat-button"
                                                onClick={handleStartChat}
                                                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all shadow-md hover:shadow-lg font-semibold"
                                            >
                                                <MessageCircle size={20} />
                                                {t('support.start_chat', 'Start Chat')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* TAB 2: Active Tickets */}
                    {activeTab === 'active' && (
                        <div data-testid="support-active-tickets-section" className="flex-1 flex overflow-hidden">
                            {/* Ticket List */}
                            <div data-testid="support-active-ticket-list-container" className="w-80 border-r border-gray-200 overflow-y-auto bg-gray-50 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-400">
                                <div data-testid="support-active-ticket-list-header" className="p-4 border-b border-gray-200 bg-white">
                                    <h3 data-testid="support-active-ticket-list-heading" className="font-semibold text-gray-800">{t('support.active_tickets')} ({tickets.activeTickets.length})</h3>
                                </div>
                                {tickets.activeTickets.length === 0 ? (
                                    <div data-testid="support-no-active-tickets" className="p-4 text-center text-gray-500 text-sm">
                                        <p data-testid="support-no-active-tickets-text">{t('support.no_active_tickets')}</p>
                                    </div>
                                ) : (
                                    <div data-testid="support-active-ticket-list" className="divide-y divide-gray-200">
                                        {tickets.activeTickets.map((ticket) => (
                                            <button
                                                data-testid={`support-active-ticket-card-${ticket.ticketId}`}
                                                key={ticket.ticketId}
                                                onClick={() => handleSelectTicket(ticket)}
                                                className={`w-full p-4 text-left hover:bg-gray-100 transition-colors ${selectedTicket?.ticketId === ticket.ticketId
                                                    ? 'bg-[#4ABDBD]/10 border-l-4 border-[#4ABDBD]'
                                                    : 'bg-white'
                                                    }`}
                                            >
                                                <div className="flex items-start justify-between mb-2">
                                                    <span data-testid={`support-active-ticket-id-${ticket.ticketId}`} className="font-semibold text-gray-800 text-sm">{ticket.ticketId}</span>
                                                    {getStatusBadge(ticket.status)}
                                                </div>
                                                {ticket.subject && (
                                                    <p data-testid={`support-active-ticket-subject-${ticket.ticketId}`} className="text-xs text-gray-700 font-medium mb-1">
                                                        <MasterLabel
                                                            type="masterSupportSubCategory"
                                                            id={ticket.subCategoryId}
                                                            field="name"
                                                            fallback={ticket.subject}
                                                        />
                                                    </p>
                                                )}
                                                {/* Only the category here — the subcategory already appears as the subject above. */}
                                                {(ticket.category || ticket.categoryId) && (
                                                    <p data-testid={`support-active-ticket-category-${ticket.ticketId}`} className="text-xs text-gray-500">
                                                        <MasterLabel
                                                            type="masterSupportCategory"
                                                            id={ticket.categoryId}
                                                            field="name"
                                                            fallback={ticket.category || ''}
                                                        />
                                                    </p>
                                                )}
                                                <p data-testid={`support-active-ticket-date-${ticket.ticketId}`} className="text-xs text-gray-400 mt-2">{formatDate(ticket.createdAt)}</p>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Chat Area */}
                            <div data-testid="support-active-chat-area" className="flex-1 flex flex-col bg-white">
                                {selectedTicket ? (
                                    <>
                                        {/* Chat Header */}
                                        <div data-testid="support-active-chat-header" className="p-4 border-b border-gray-200 bg-gradient-to-r from-[#4ABDBD]/10 to-[#3AA8A8]/10">
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <h3 data-testid="support-active-chat-ticket-id" className="font-semibold text-gray-800">{selectedTicket.ticketId}</h3>
                                                    {selectedTicket.subject && (
                                                        <p data-testid="support-active-chat-subject" className="text-xs text-gray-700 mt-1 font-medium">
                                                            {t('support.subject_label')}{' '}
                                                            <MasterLabel
                                                                type="masterSupportSubCategory"
                                                                id={selectedTicket.subCategoryId}
                                                                field="name"
                                                                fallback={selectedTicket.subject}
                                                            />
                                                        </p>
                                                    )}
                                                    {/* Only the category here — the subcategory already appears in the Subject line above. */}
                                                    {(selectedTicket.category || selectedTicket.categoryId) && (
                                                        <p data-testid="support-active-chat-category" className="text-xs text-gray-500 mt-0.5">
                                                            <MasterLabel
                                                                type="masterSupportCategory"
                                                                id={selectedTicket.categoryId}
                                                                field="name"
                                                                fallback={selectedTicket.category || ''}
                                                            />
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {getStatusBadge(selectedTicket.status)}
                                                    {/* {selectedTicket.status === 'assigned' && ( */}
                                                    <button
                                                        data-testid="support-close-ticket-button"
                                                        onClick={handleCloseTicket}
                                                        className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all text-sm font-medium flex items-center gap-1"
                                                    >
                                                        <XCircle size={16} />
                                                        {t('support.close_ticket')}
                                                    </button>
                                                    {/* )} */}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Messages */}
                                        <div data-testid="support-active-messages-container" className="flex-1 relative min-h-0 flex flex-col">
                                            <div
                                                data-testid="support-active-messages-list"
                                                ref={chatAreaRef}
                                                className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-400"
                                                onScroll={handleScroll}
                                            >
                                                {selectedTicket.messages?.map((message, index) => {
                                                    console.log(message);
                                                    const isUser = message.senderRole === userRole;
                                                    const isBot = message.senderRole === 'bot';
                                                    const isAdmin = message.senderRole === 'admin';
                                                    const isSystem = message.senderRole === 'system';

                                                    // System banner (assignment / reassignment) — centered pill.
                                                    if (isSystem) {
                                                        const evt = message.systemEvent || {};
                                                        const isAssignEvt = evt.type === 'assigned';
                                                        const isReassignEvt = evt.type === 'reassigned';
                                                        return (
                                                            <div
                                                                data-testid={`support-active-system-message-${index}`}
                                                                id={`message-${message.messageId}`}
                                                                key={index}
                                                                className="flex justify-center my-3"
                                                            >
                                                                <div className="max-w-[85%] px-4 py-2 rounded-full bg-gradient-to-r from-[#4ABDBD]/10 to-[#3AA8A8]/10 border border-[#4ABDBD]/30 text-center">
                                                                    {isAssignEvt && (
                                                                        <p data-testid={`support-active-system-assigned-text-${index}`} className="text-xs text-gray-700">
                                                                            <span className="font-semibold text-[#2D8A8A]">
                                                                                {t('support.ticket_assigned_banner', { defaultValue: 'Ticket assigned' })}
                                                                            </span>
                                                                            {evt.toAdminName && (
                                                                                <>
                                                                                    <span className="mx-1">·</span>
                                                                                    {t('support.handled_by', { defaultValue: 'Handled by' })}{' '}
                                                                                    <span className="font-semibold">{evt.toAdminName}</span>
                                                                                </>
                                                                            )}
                                                                        </p>
                                                                    )}
                                                                    {isReassignEvt && (
                                                                        <p data-testid={`support-active-system-reassigned-text-${index}`} className="text-xs text-gray-700">
                                                                            <span className="font-semibold text-[#2D8A8A]">
                                                                                {t('support.ticket_reassigned_banner', { defaultValue: 'Ticket reassigned' })}
                                                                            </span>
                                                                            {evt.toAdminName && (
                                                                                <>
                                                                                    <span className="mx-1">·</span>
                                                                                    {t('support.now_handled_by', { defaultValue: 'Now handled by' })}{' '}
                                                                                    <span className="font-semibold">{evt.toAdminName}</span>
                                                                                </>
                                                                            )}
                                                                        </p>
                                                                    )}
                                                                    {!isAssignEvt && !isReassignEvt && (
                                                                        <p data-testid={`support-active-system-message-text-${index}`} className="text-xs text-gray-600">{message.message}</p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    }

                                                    // Get sender display name
                                                    let senderName = '';
                                                    if (isUser) {
                                                        senderName = t('support.sender_you', 'You');
                                                    } else {
                                                        const name = message.senderName || (message.senderRole ? message.senderRole.charAt(0).toUpperCase() + message.senderRole.slice(1) : t('support.sender_user', 'User'));
                                                        if (isAdmin) senderName = name;
                                                        else if (isBot) senderName = name;
                                                        else if (message.senderRole === 'doctor') senderName = name;
                                                        else if (message.senderRole === 'patient') senderName = name;
                                                        else senderName = name;
                                                    }

                                                    return (
                                                        <div
                                                            data-testid={`support-active-message-${index}`}
                                                            id={`message-${message.messageId}`}
                                                            key={index}
                                                            className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3 group/msg py-1.5`}
                                                        >
                                                            <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} max-w-[70%] relative`}>
                                                                {/* Sender Name (only for received messages) */}
                                                                {!isUser && (
                                                                    <span data-testid={`support-active-message-sender-${index}`} className="text-xs font-bold text-[#4ABDBD] mb-1 px-2">
                                                                        {senderName}
                                                                    </span>
                                                                )}

                                                                {/* Message Bubble */}
                                                                <div
                                                                    data-testid={`support-active-message-bubble-${index}`}
                                                                    className={`rounded-2xl px-4 py-2 shadow-sm relative transition-all duration-300 ${isUser
                                                                        ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-br-md'
                                                                        : isBot
                                                                            ? 'bg-blue-50 text-blue-900 border border-blue-200 rounded-bl-md'
                                                                            : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md'
                                                                        }`}
                                                                >
                                                                    {/* Reply To Reference inside bubble */}
                                                                    {message.replyTo && typeof message.replyTo === 'object' && (
                                                                        <div
                                                                            data-testid={`support-active-message-replyto-${index}`}
                                                                            onClick={() => scrollToMessage(message.replyTo.messageId || message.replyTo._id)}
                                                                            className={`mb-2 p-2 rounded-xl text-xs border-l-4 cursor-pointer hover:opacity-80 transition-opacity ${isUser ? 'bg-white/10 border-white/40 text-white/90' : 'bg-gray-50 border-[#4ABDBD] text-gray-600'
                                                                                }`}>
                                                                            <div data-testid={`support-active-message-replyto-sender-${index}`} className="font-semibold text-[10px] mb-0.5 opacity-80 flex items-center gap-1">
                                                                                <Reply size={10} />
                                                                                {message.replyTo.senderRole === userRole ? t('support.sender_you', 'You') : (message.replyTo.senderName || (message.replyTo.senderRole === 'admin' ? t('support.sender_admin', 'Admin') : (message.replyTo.senderRole === 'bot' ? t('support.sender_support_bot', 'Support Bot') : (message.replyTo.senderRole === 'doctor' ? t('support.sender_doctor', 'Doctor') : (message.replyTo.senderRole === 'patient' ? t('support.sender_patient', 'Patient') : t('support.sender_user', 'User'))))))}
                                                                            </div>
                                                                            <div data-testid={`support-active-message-replyto-text-${index}`} className="line-clamp-2 truncate">{message.replyTo.message || t('support.attachment', 'Attachment')}</div>
                                                                        </div>
                                                                    )}
                                                                    <p data-testid={`support-active-message-text-${index}`} className="text-sm whitespace-pre-wrap break-words leading-relaxed">{message.message}</p>

                                                                    {/* Attachments */}
                                                                    {message.attachments && message.attachments.length > 0 && (
                                                                        <div data-testid={`support-active-message-attachments-${index}`} className="mt-3 grid gap-2 min-w-[200px]">
                                                                            {message.attachments.map((file, i) => (
                                                                                <div data-testid={`support-active-message-attachment-${index}-${i}`} key={i} className={`rounded-xl overflow-hidden border ${isUser ? 'border-white/20 bg-white/10' : 'border-gray-100 bg-gray-50'}`}>
                                                                                    {file.fileType === 'image' || (file.mimeType && file.mimeType.startsWith('image/')) ? (
                                                                                        <div data-testid={`support-active-message-image-${index}-${i}`} className="relative group cursor-pointer" onClick={() => setPreviewImage(getFileURL(file.url))}>
                                                                                            <img
                                                                                                data-testid={`support-active-message-image-preview-${index}-${i}`}
                                                                                                src={getFileURL(file.url)}
                                                                                                alt={file.fileName || t('support.image_alt', 'Image')}
                                                                                                className="max-w-full h-auto max-h-52 object-contain mx-auto"
                                                                                            />
                                                                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                                                                                <button data-testid={`support-active-message-image-view-button-${index}-${i}`} className="p-2.5 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40 shadow-sm transition-all scale-90 group-hover:scale-100">
                                                                                                    <Eye size={20} />
                                                                                                </button>
                                                                                                <button
                                                                                                    data-testid={`support-active-message-image-download-button-${index}-${i}`}
                                                                                                    className="p-2.5 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40 shadow-sm transition-all scale-90 group-hover:scale-100"
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        handleDownload(file.url);
                                                                                                    }}
                                                                                                >
                                                                                                    <Download size={20} />
                                                                                                </button>
                                                                                            </div>
                                                                                        </div>
                                                                                    ) : file.fileType === 'voice' || (file.mimeType && file.mimeType.startsWith('audio/')) ? (
                                                                                        <div data-testid={`support-active-message-voice-${index}-${i}`} className={`p-3 min-w-[240px] ${isUser ? 'bg-white/10' : 'bg-[#4ABDBD]/5'} rounded-xl`}>
                                                                                            <div className="flex items-center gap-2 mb-2">
                                                                                                <Volume2 size={16} className={isUser ? 'text-white' : 'text-[#4ABDBD]'} />
                                                                                                <span data-testid={`support-active-message-voice-label-${index}-${i}`} className={`text-xs font-bold ${isUser ? 'text-white' : 'text-gray-700'}`}>
                                                                                                    {t('chat.voice_message', 'Voice Message')}
                                                                                                </span>
                                                                                            </div>
                                                                                            <audio
                                                                                                data-testid={`support-active-message-voice-audio-${index}-${i}`}
                                                                                                src={getFileURL(file.url)}
                                                                                                controls
                                                                                                className={`w-[255px] h-12 custom-audio-player ${isUser ? 'invert brightness-200' : ''}`}
                                                                                            />
                                                                                        </div>
                                                                                    ) : file.fileType === 'voice' || (file.mimeType && file.mimeType.startsWith('audio/')) ? (
                                                                                        <div data-testid={`support-active-message-voice-alt-${index}-${i}`} className={`p-3 min-w-[240px] ${isUser ? 'bg-white/10' : 'bg-[#4ABDBD]/5'} rounded-xl`}>
                                                                                            <div className="flex items-center gap-2 mb-2">
                                                                                                <Volume2 size={16} className={isUser ? 'text-white' : 'text-[#4ABDBD]'} />
                                                                                                <span data-testid={`support-active-message-voice-alt-label-${index}-${i}`} className={`text-xs font-bold ${isUser ? 'text-white' : 'text-gray-700'}`}>
                                                                                                    {t('chat.voice_message', 'Voice Message')}
                                                                                                </span>
                                                                                            </div>
                                                                                            <audio
                                                                                                data-testid={`support-active-message-voice-alt-audio-${index}-${i}`}
                                                                                                src={getFileURL(file.url)}
                                                                                                controls
                                                                                                className={`w-[255px] h-12 custom-audio-player ${isUser ? 'invert brightness-200' : ''}`}
                                                                                            />
                                                                                        </div>
                                                                                    ) : (
                                                                                        <div data-testid={`support-active-message-file-${index}-${i}`} className="p-3 flex items-center gap-3">
                                                                                            <div className={`p-2 rounded-lg ${isUser ? 'bg-white/20' : 'bg-white shadow-sm'}`}>
                                                                                                <File size={20} />
                                                                                            </div>
                                                                                            <div className="flex-1 min-w-0">
                                                                                                <p data-testid={`support-active-message-file-name-${index}-${i}`} className="text-xs font-bold truncate">{file.fileName}</p>
                                                                                                <p data-testid={`support-active-message-file-type-${index}-${i}`} className="text-[10px] opacity-60 uppercase">{file.mimeType ? file.mimeType.split('/')[1] : 'FILE'}</p>
                                                                                            </div>
                                                                                            <button
                                                                                                data-testid={`support-active-message-file-download-button-${index}-${i}`}
                                                                                                className={`p-2 rounded-lg transition-colors ${isUser ? 'hover:bg-white/20' : 'hover:bg-gray-200'}`}
                                                                                                onClick={() => handleDownload(file.url)}
                                                                                            >
                                                                                                <Download size={18} />
                                                                                            </button>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}

                                                                    <p data-testid={`support-active-message-time-${index}`} className={`text-xs mt-1.5 ${isUser ? 'text-white/70' : 'text-gray-500'
                                                                        }`}>
                                                                        {formatTime(message.createdAt || message.time)}
                                                                    </p>
                                                                </div>

                                                                {/* Reply Button (Hover) */}
                                                                {message.senderRole !== 'bot' && (
                                                                    <div className={`absolute top-1/2 -translate-y-1/2 opacity-0 group-hover/msg:opacity-100 transition-opacity flex items-center gap-1 z-10 ${isUser ? 'right-[calc(100%+8px)]' : 'left-[calc(100%+8px)]'}`}>
                                                                        <button
                                                                            data-testid={`support-active-message-reply-button-${index}`}
                                                                            onClick={() => setReplyToMessage(message)}
                                                                            className="p-1.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-full shadow-sm transition-all"
                                                                            title={t('support.reply', 'Reply')}
                                                                        >
                                                                            <Reply size={14} />
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                <div ref={messagesEndRef} />
                                            </div>
                                            <AnimatePresence>
                                                {showScrollBottom && (
                                                    <motion.button
                                                        data-testid="support-scroll-to-bottom-button"
                                                        initial={{ opacity: 0, scale: 0.8, y: 10 }}
                                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                                        exit={{ opacity: 0, scale: 0.8, y: 10 }}
                                                        onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })}
                                                        className="absolute bottom-4 right-6 p-3 bg-[#4ABDBD] text-white rounded-full shadow-lg hover:bg-[#3A9B9B] transition-all z-10 flex items-center justify-center group"
                                                        title={t('chat.recent_messages', 'Recent messages')}
                                                    >
                                                        <ChevronDown size={20} className="group-hover:translate-y-0.5 transition-transform" />
                                                    </motion.button>
                                                )}
                                            </AnimatePresence>
                                        </div>

                                        {/* Input Area */}
                                        <div data-testid="support-input-area" className="p-4 border-t border-gray-200 bg-white relative">
                                            {/* Reply to preview in input box area */}
                                            {replyToMessage && (
                                                <div data-testid="support-reply-preview-container" className="mb-3 px-4 py-3 bg-gray-50 rounded-xl border border-gray-200 flex items-start justify-between shadow-sm animate-in slide-in-from-bottom-2">
                                                    <div className="flex-1 min-w-0 pr-4">
                                                        <div data-testid="support-reply-preview-sender" className="flex items-center gap-1 text-xs font-semibold text-[#4ABDBD] mb-1">
                                                            <Reply size={12} />
                                                            {t('support.replying_to', 'Replying to')} {replyToMessage.senderRole === userRole ? t('support.sender_you', 'You') : (replyToMessage.senderName || (replyToMessage.senderRole === 'admin' ? t('support.sender_admin', 'Admin') : (replyToMessage.senderRole === 'bot' ? t('support.sender_support_bot', 'Support Bot') : (replyToMessage.senderRole === 'doctor' ? t('support.sender_doctor', 'Doctor') : (replyToMessage.senderRole === 'patient' ? t('support.sender_patient', 'Patient') : t('support.sender_user', 'User'))))))}
                                                        </div>
                                                        <p data-testid="support-reply-preview-text" className="text-sm text-gray-600 truncate">{replyToMessage.message || t('support.attachment', 'Attachment')}</p>
                                                    </div>
                                                    <button
                                                        data-testid="support-reply-preview-cancel-button"
                                                        onClick={() => setReplyToMessage(null)}
                                                        className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            )}

                                            {/* File Previews */}
                                            {selectedFiles.length > 0 && (
                                                <div data-testid="support-selected-files-container" className="mb-3 flex flex-wrap gap-2 animate-in slide-in-from-bottom-2">
                                                    {selectedFiles.map((file, index) => (
                                                        <div data-testid={`support-selected-file-${index}`} key={index} className="relative group bg-gray-50 border border-gray-200 rounded-xl p-2 pr-9 flex items-center gap-2 shadow-sm">
                                                            {file.type.startsWith('image/') ? (
                                                                <div className="w-8 h-8 rounded-lg bg-[#4ABDBD]/10 flex items-center justify-center overflow-hidden">
                                                                    <ImageIcon data-testid={`support-selected-file-image-icon-${index}`} size={16} className="text-[#4ABDBD]" />
                                                                </div>
                                                            ) : (
                                                                <div className="w-8 h-8 rounded-lg bg-gray-200 flex items-center justify-center">
                                                                    <File data-testid={`support-selected-file-icon-${index}`} size={16} className="text-gray-500" />
                                                                </div>
                                                            )}
                                                            <span data-testid={`support-selected-file-name-${index}`} className="text-[11px] font-bold text-gray-700 truncate max-w-[100px]">{file.name}</span>
                                                            <button
                                                                data-testid={`support-selected-file-remove-button-${index}`}
                                                                type="button"
                                                                onClick={() => removeFile(index)}
                                                                className="absolute right-1 text-gray-400 hover:text-red-500 p-1 transition-colors"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <form data-testid="support-message-form" onSubmit={handleSendMessage} className="flex flex-col gap-2">
                                                <div className="flex gap-2 items-center">
                                                    <input
                                                        data-testid="support-file-input"
                                                        type="file"
                                                        ref={fileInputRef}
                                                        onChange={handleFileChange}
                                                        multiple
                                                        className="hidden"
                                                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                                                    />
                                                    <button
                                                        data-testid="support-attach-file-button"
                                                        type="button"
                                                        onClick={() => fileInputRef.current?.click()}
                                                        className="p-2 text-gray-400 hover:text-[#4ABDBD] hover:bg-[#4ABDBD]/5 rounded-xl transition-all border border-gray-200"
                                                        title={t('chat.attach_files', 'Attach Files')}
                                                    >
                                                        <Paperclip size={20} />
                                                    </button>

                                                    <input
                                                        data-testid="support-message-input"
                                                        type="text"
                                                        value={messageInput}
                                                        onChange={handleMessageChange}
                                                        maxLength={1000}
                                                        disabled={isUploading}
                                                        placeholder={t('support.type_message_placeholder')}
                                                        className={`flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 ${messageError
                                                            ? 'border-red-300 bg-red-50 focus:ring-red-500'
                                                            : 'border-gray-300 focus:ring-[#4ABDBD]'
                                                            }`}
                                                    />
                                                    {messageInput.trim() || selectedFiles.length > 0 ? (
                                                        <button
                                                            data-testid="support-send-message-button"
                                                            type="submit"
                                                            disabled={isUploading}
                                                            className={`px-6 h-[42px] flex items-center justify-center bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-lg hover:from-[#3AA8A8] hover:to-[#2A8A8A] transition-all ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                        >
                                                            {isUploading ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                                                        </button>
                                                    ) : (
                                                        <VoiceRecorder
                                                            isUploading={isUploading}
                                                            disabled={isUploading}
                                                            onRecordingComplete={(file) => {
                                                                const formData = new FormData();
                                                                const userId = localStorage.getItem('ID');
                                                                formData.append('ticketId', selectedTicket.ticketId);
                                                                formData.append('senderId', userId);
                                                                formData.append('senderRole', userRole);
                                                                formData.append('files', file);

                                                                setIsUploading(true);
                                                                axios.post(`${baseURL}/support/message/send`, formData, {
                                                                    headers: { 'Content-Type': 'multipart/form-data' },
                                                                }).then(() => {
                                                                    // Handle success
                                                                }).catch(err => {
                                                                    console.error("Error sending voice note:", err);
                                                                    toast.warning(t('support.send_failed'));
                                                                }).finally(() => {
                                                                    setIsUploading(false);
                                                                });
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                {messageError && (
                                                    <p data-testid="support-message-error" className="text-xs text-red-600">{messageError}</p>
                                                )}
                                            </form>
                                        </div>
                                    </>
                                ) : (
                                    <div data-testid="support-active-empty-state" className="flex-1 flex items-center justify-center text-gray-500">
                                        <div className="text-center">
                                            <MessageCircle data-testid="support-active-empty-state-icon" size={48} className="mx-auto mb-4 text-gray-300" />
                                            <p data-testid="support-active-empty-state-text">{t('support.select_ticket_message')}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* TAB 3: Closed Tickets */}
                    {activeTab === 'closed' && (
                        <div data-testid="support-closed-tickets-section" className="flex-1 flex overflow-hidden">
                            {/* Ticket List */}
                            <div data-testid="support-closed-ticket-list-container" className="w-80 border-r border-gray-200 overflow-y-auto bg-gray-50 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-400">
                                <div data-testid="support-closed-ticket-list-header" className="p-4 border-b border-gray-200 bg-white">
                                    <h3 data-testid="support-closed-ticket-list-heading" className="font-semibold text-gray-800">{t('support.closed_tickets')} ({tickets.closedTickets.length})</h3>
                                </div>
                                {tickets.closedTickets.length === 0 ? (
                                    <div data-testid="support-no-closed-tickets" className="p-4 text-center text-gray-500 text-sm">
                                        <p data-testid="support-no-closed-tickets-text">{t('support.no_closed_tickets', 'No closed tickets.')}</p>
                                    </div>
                                ) : (
                                    <div data-testid="support-closed-ticket-list" className="divide-y divide-gray-200">
                                        {tickets.closedTickets.map((ticket) => (
                                            <button
                                                data-testid={`support-closed-ticket-card-${ticket.ticketId}`}
                                                key={ticket.ticketId}
                                                onClick={() => handleSelectTicket(ticket)}
                                                className={`w-full p-4 text-left hover:bg-gray-100 transition-colors ${selectedTicket?.ticketId === ticket.ticketId
                                                    ? 'bg-[#4ABDBD]/10 border-l-4 border-[#4ABDBD]'
                                                    : 'bg-white'
                                                    }`}
                                            >
                                                <div className="flex items-start justify-between mb-2">
                                                    <span data-testid={`support-closed-ticket-id-${ticket.ticketId}`} className="font-semibold text-gray-800 text-sm">{ticket.ticketId}</span>
                                                    {getStatusBadge(ticket.status)}
                                                </div>
                                                {ticket.subject && (
                                                    <p data-testid={`support-closed-ticket-subject-${ticket.ticketId}`} className="text-xs text-gray-700 font-medium mb-1">
                                                        <MasterLabel
                                                            type="masterSupportSubCategory"
                                                            id={ticket.subCategoryId}
                                                            field="name"
                                                            fallback={ticket.subject}
                                                        />
                                                    </p>
                                                )}
                                                {/* Only the category here — the subcategory already appears as the subject above. */}
                                                {(ticket.category || ticket.categoryId) && (
                                                    <p data-testid={`support-closed-ticket-category-${ticket.ticketId}`} className="text-xs text-gray-500">
                                                        <MasterLabel
                                                            type="masterSupportCategory"
                                                            id={ticket.categoryId}
                                                            field="name"
                                                            fallback={ticket.category || ''}
                                                        />
                                                    </p>
                                                )}
                                                <p data-testid={`support-closed-ticket-date-${ticket.ticketId}`} className="text-xs text-gray-400 mt-2">{t('support.closed_prefix')} {formatDate(ticket.closedAt)}</p>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Chat Area (Read-only) */}
                            <div data-testid="support-closed-chat-area" className="flex-1 flex flex-col bg-white">
                                {selectedTicket ? (
                                    <>
                                        {/* Chat Header */}
                                        <div data-testid="support-closed-chat-header" className="p-4 border-b border-gray-200 bg-gradient-to-r from-[#4ABDBD]/10 to-[#3AA8A8]/10">
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h3 data-testid="support-closed-chat-ticket-id" className="font-semibold text-gray-800">{selectedTicket.ticketId}</h3>
                                                        <span data-testid="support-closed-chat-closed-label" className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full font-medium">
                                                            {t('support.ticket_closed')}
                                                        </span>
                                                    </div>
                                                    {selectedTicket.subject && (
                                                        <p data-testid="support-closed-chat-subject" className="text-xs text-gray-700 mt-1 font-medium">
                                                            {t('support.subject_label')}{' '}
                                                            <MasterLabel
                                                                type="masterSupportSubCategory"
                                                                id={selectedTicket.subCategoryId}
                                                                field="name"
                                                                fallback={selectedTicket.subject}
                                                            />
                                                        </p>
                                                    )}
                                                    {/* Only the category here — the subcategory already appears in the Subject line above. */}
                                                    {(selectedTicket.category || selectedTicket.categoryId) && (
                                                        <p data-testid="support-closed-chat-category" className="text-xs text-gray-500 mt-0.5">
                                                            <MasterLabel
                                                                type="masterSupportCategory"
                                                                id={selectedTicket.categoryId}
                                                                field="name"
                                                                fallback={selectedTicket.category || ''}
                                                            />
                                                        </p>
                                                    )}
                                                </div>
                                                {getStatusBadge(selectedTicket.status)}
                                            </div>
                                        </div>

                                        {/* Messages */}
                                        <div data-testid="support-closed-messages-list" ref={chatAreaRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:hover:bg-gray-400">
                                            {selectedTicket.messages?.map((message, index) => {
                                                const isUser = message.senderRole === userRole;
                                                const isBot = message.senderRole === 'bot';
                                                const isAdmin = message.senderRole === 'admin';
                                                const isSystem = message.senderRole === 'system';

                                                // System banner (assignment / reassignment) — centered pill.
                                                if (isSystem) {
                                                    const evt = message.systemEvent || {};
                                                    const isAssignEvt = evt.type === 'assigned';
                                                    const isReassignEvt = evt.type === 'reassigned';
                                                    return (
                                                        <div
                                                            data-testid={`support-closed-system-message-${index}`}
                                                            id={`message-${message.messageId}`}
                                                            key={index}
                                                            className="flex justify-center my-3"
                                                        >
                                                            <div className="max-w-[85%] px-4 py-2 rounded-full bg-gradient-to-r from-[#4ABDBD]/10 to-[#3AA8A8]/10 border border-[#4ABDBD]/30 text-center">
                                                                {isAssignEvt && (
                                                                    <p data-testid={`support-closed-system-assigned-text-${index}`} className="text-xs text-gray-700">
                                                                        <span className="font-semibold text-[#2D8A8A]">
                                                                            {t('support.ticket_assigned_banner', { defaultValue: 'Ticket assigned' })}
                                                                        </span>
                                                                        {evt.toAdminName && (
                                                                            <>
                                                                                <span className="mx-1">·</span>
                                                                                {t('support.handled_by', { defaultValue: 'Handled by' })}{' '}
                                                                                <span className="font-semibold">{evt.toAdminName}</span>
                                                                            </>
                                                                        )}
                                                                    </p>
                                                                )}
                                                                {isReassignEvt && (
                                                                    <p data-testid={`support-closed-system-reassigned-text-${index}`} className="text-xs text-gray-700">
                                                                        <span className="font-semibold text-[#2D8A8A]">
                                                                            {t('support.ticket_reassigned_banner', { defaultValue: 'Ticket reassigned' })}
                                                                        </span>
                                                                        {evt.toAdminName && (
                                                                            <>
                                                                                <span className="mx-1">·</span>
                                                                                {t('support.now_handled_by', { defaultValue: 'Now handled by' })}{' '}
                                                                                <span className="font-semibold">{evt.toAdminName}</span>
                                                                            </>
                                                                        )}
                                                                    </p>
                                                                )}
                                                                {!isAssignEvt && !isReassignEvt && (
                                                                    <p data-testid={`support-closed-system-message-text-${index}`} className="text-xs text-gray-600">{message.message}</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                let senderName = '';
                                                if (isUser) {
                                                    senderName = t('support.sender_you', 'You');
                                                } else {
                                                    const name = message.senderName || (message.senderRole ? message.senderRole.charAt(0).toUpperCase() + message.senderRole.slice(1) : t('support.sender_user', 'User'));
                                                    if (isAdmin) senderName = name;
                                                    else if (isBot) senderName = name;
                                                    else if (message.senderRole === 'doctor') senderName = name;
                                                    else if (message.senderRole === 'patient') senderName = name;
                                                    else senderName = name;
                                                }

                                                return (
                                                    <div
                                                        data-testid={`support-closed-message-${index}`}
                                                        id={`message-${message.messageId}`}
                                                        key={index}
                                                        className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3 group/msg py-1.5`}
                                                    >
                                                        <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} max-w-[70%] relative`}>
                                                            {/* Sender Name (only for received messages) */}
                                                            {!isUser && (
                                                                <span data-testid={`support-closed-message-sender-${index}`} className="text-xs font-bold text-[#4ABDBD] mb-1 px-2">
                                                                    {senderName}
                                                                </span>
                                                            )}

                                                            {/* Message Bubble */}
                                                            <div
                                                                data-testid={`support-closed-message-bubble-${index}`}
                                                                className={`rounded-2xl px-4 py-2 shadow-sm relative transition-all duration-300 ${isUser
                                                                    ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white rounded-br-md'
                                                                    : isBot
                                                                        ? 'bg-blue-50 text-blue-900 border border-blue-200 rounded-bl-md'
                                                                        : 'bg-white text-gray-800 border border-gray-200 rounded-bl-md'
                                                                    }`}
                                                            >
                                                                {/* Reply To Reference inside bubble */}
                                                                {message.replyTo && typeof message.replyTo === 'object' && (
                                                                    <div
                                                                        data-testid={`support-closed-message-replyto-${index}`}
                                                                        onClick={() => scrollToMessage(message.replyTo.messageId || message.replyTo._id)}
                                                                        className={`mb-2 p-2 rounded-xl text-xs border-l-4 cursor-pointer hover:opacity-80 transition-opacity ${isUser ? 'bg-white/10 border-white/40 text-white/90' : 'bg-gray-50 border-[#4ABDBD] text-gray-600'
                                                                            }`}
                                                                    >
                                                                        <div data-testid={`support-closed-message-replyto-sender-${index}`} className="font-semibold text-[10px] mb-0.5 opacity-80 flex items-center gap-1">
                                                                            <Reply size={10} />
                                                                            {message.replyTo.senderRole === userRole ? t('support.sender_you', 'You') : (message.replyTo.senderName || (message.replyTo.senderRole === 'admin' ? t('support.sender_admin', 'Admin') : (message.replyTo.senderRole === 'bot' ? t('support.sender_support_bot', 'Support Bot') : (message.replyTo.senderRole === 'doctor' ? t('support.sender_doctor', 'Doctor') : (message.replyTo.senderRole === 'patient' ? t('support.sender_patient', 'Patient') : t('support.sender_user', 'User'))))))}
                                                                        </div>
                                                                        <div data-testid={`support-closed-message-replyto-text-${index}`} className="line-clamp-2 truncate">{message.replyTo.message || t('support.attachment', 'Attachment')}</div>
                                                                    </div>
                                                                )}
                                                                <p data-testid={`support-closed-message-text-${index}`} className="text-sm whitespace-pre-wrap break-words leading-relaxed">{message.message}</p>

                                                                {/* Attachments */}
                                                                {message.attachments && message.attachments.length > 0 && (
                                                                    <div data-testid={`support-closed-message-attachments-${index}`} className="mt-3 grid gap-2 min-w-[200px]">
                                                                        {message.attachments.map((file, i) => (
                                                                            <div data-testid={`support-closed-message-attachment-${index}-${i}`} key={i} className={`rounded-xl overflow-hidden border ${isUser ? 'border-white/20 bg-white/10' : 'border-gray-100 bg-gray-50'}`}>
                                                                                {file.fileType === 'image' || (file.mimeType && file.mimeType.startsWith('image/')) ? (
                                                                                    <div data-testid={`support-closed-message-image-${index}-${i}`} className="relative group cursor-pointer" onClick={() => setPreviewImage(getFileURL(file.url))}>
                                                                                        <img
                                                                                            data-testid={`support-closed-message-image-preview-${index}-${i}`}
                                                                                            src={getFileURL(file.url)}
                                                                                            alt={file.fileName || t('support.image_alt', 'Image')}
                                                                                            className="max-w-full h-auto max-h-52 object-contain mx-auto"
                                                                                        />
                                                                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                                                                            <button data-testid={`support-closed-message-image-view-button-${index}-${i}`} className="p-2.5 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40 shadow-sm transition-all scale-90 group-hover:scale-100">
                                                                                                <Eye size={20} />
                                                                                            </button>
                                                                                            <button
                                                                                                data-testid={`support-closed-message-image-download-button-${index}-${i}`}
                                                                                                className="p-2.5 bg-white/20 backdrop-blur-md rounded-full text-white hover:bg-white/40 shadow-sm transition-all scale-90 group-hover:scale-100"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    handleDownload(file.url);
                                                                                                }}
                                                                                            >
                                                                                                <Download size={20} />
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div data-testid={`support-closed-message-file-${index}-${i}`} className="p-3 flex items-center gap-3">
                                                                                        <div className={`p-2 rounded-lg ${isUser ? 'bg-white/20' : 'bg-white shadow-sm'}`}>
                                                                                            <File size={20} />
                                                                                        </div>
                                                                                        <div className="flex-1 min-w-0">
                                                                                            <p data-testid={`support-closed-message-file-name-${index}-${i}`} className="text-xs font-bold truncate">{file.fileName}</p>
                                                                                            <p data-testid={`support-closed-message-file-type-${index}-${i}`} className="text-[10px] opacity-60 uppercase">{file.mimeType ? file.mimeType.split('/')[1] : 'FILE'}</p>
                                                                                        </div>
                                                                                        <button
                                                                                            data-testid={`support-closed-message-file-download-button-${index}-${i}`}
                                                                                            className={`p-2 rounded-lg transition-colors ${isUser ? 'hover:bg-white/20' : 'hover:bg-gray-200'}`}
                                                                                            onClick={() => handleDownload(file.url)}
                                                                                        >
                                                                                            <Download size={18} />
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}

                                                                <p data-testid={`support-closed-message-time-${index}`} className={`text-xs mt-1.5 ${isUser ? 'text-white/70' : 'text-gray-500'
                                                                    }`}>
                                                                    {formatTime(message.createdAt || message.time)}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <div ref={messagesEndRef} />
                                        </div>

                                        {/* Disabled Input Area */}
                                        {/* <div className="p-4 border-t border-gray-200 bg-gray-100">
                                        <div className="flex gap-2 opacity-50">
                                            <input
                                                type="text"
                                                disabled
                                                placeholder={t('support.ticket_is_closed')}
                                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-200 cursor-not-allowed"
                                            />
                                            <button
                                                disabled
                                                className="px-6 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed"
                                            >
                                                <Send size={20} />
                                            </button>
                                        </div>
                                    </div> */}
                                    </>
                                ) : (
                                    <div data-testid="support-closed-empty-state" className="flex-1 flex items-center justify-center text-gray-500">
                                        <div className="text-center">
                                            <MessageCircle data-testid="support-closed-empty-state-icon" size={48} className="mx-auto mb-4 text-gray-300" />
                                            <p data-testid="support-closed-empty-state-text">{t('support.select_closed_ticket')}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Image Preview Modal */}
            <AnimatePresence>
                {previewImage && (
                    <motion.div
                        data-testid="support-image-preview-modal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
                        onClick={() => setPreviewImage(null)}
                    >
                        <motion.div
                            data-testid="support-image-preview-modal-content"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="relative max-w-4xl w-full max-h-[90vh] flex items-center justify-center"
                            onClick={e => e.stopPropagation()}
                        >
                            <button
                                data-testid="support-image-preview-modal-close-button"
                                className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors"
                                onClick={() => setPreviewImage(null)}
                            >
                                <X size={24} />
                            </button>
                            <img
                                data-testid="support-image-preview-image"
                                src={previewImage}
                                alt={t('chat.preview', 'Preview')}
                                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
                            />
                            <div className="absolute -bottom-12 left-0 right-0 flex justify-center">
                                <button
                                    data-testid="support-image-preview-download-button"
                                    className="px-6 py-2 bg-white text-gray-900 rounded-full font-medium shadow-lg hover:bg-gray-100 transition-colors flex items-center gap-2"
                                    onClick={() => handleDownload(previewImage)}
                                >
                                    <Download size={18} />
                                    {t('chat.download', 'Download')}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default PatientDoctorSupport;

